import {
  AccountPayableModel,
  AccountPayableMutationInput,
} from 'api-hooks/account-payable';
import { PaymentMethodEnum } from 'api-hooks/payment-channel';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, FormState } from 'components/elements';
import Text from 'components/elements/text';
import FormHeader from 'components/widgets/form-header';
import useApprovalAction from 'hooks/use-approval-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useVoidAction from 'hooks/use-void-action';
import useYupValidationResolver from 'hooks/use-yup-validation-resolver';
import {
  HalfContainer,
  SectionContainer,
  SeparatorVertical,
  TitleContainer,
} from 'legacy-modules/styles';
import TransactionPillStatus from 'legacy-modules/transactions/component/status';
import { AccountPayableTableForm } from 'legacy-modules/transactions/component/table-form-components';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { accountPayableSchema, FormType } from './form-type';
import InformationField from './information-field';
import PaymentField from './payment-field';
import SummaryCard from './summary-card';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  accountPayable?: AccountPayableModel;
  onSubmit: (
    input: AccountPayableMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<AccountPayableModel | undefined>;
}

export default function AccountPayableForm(props: Props) {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const { accountPayable } = props;

  const defaultValues = useGetDefaultValues({ accountPayable });

  const resolver = useYupValidationResolver(accountPayableSchema());

  const methods = useForm({
    resolver,
    defaultValues,
    mode: 'all',
  });

  const agentId = methods.getValues('agentId');
  React.useEffect(() => {
    if (!agentId || agentId === '') {
      methods.reset();
    }
  }, [agentId, methods]);

  const onSubmit = React.useCallback(
    async (values: FormType) => {
      try {
        const paymentItems = values.paymentItems.filter((item) => item.isCheck);
        values.needApproval =
          values.transactionAt.getTime() !== values.tempTransactionAt.getTime();
        if (paymentItems.length <= 0) {
          methods.setError('paymentItems', {
            message: t('error:at_least_select_one_item'),
          });
          return;
        }
        await props.onSubmit(
          {
            ...values,
            paymentItems,
            paymentableId:
              values.paymentMethod === PaymentMethodEnum.Balance
                ? values.agentId
                : values.paymentableId,
          } as AccountPayableMutationInput,
          methods as any,
        );
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props, t],
  );

  const permission = useGetCrudPermission('account-payables');
  const { onVoid, voidMutate, isActive, voidPermission } = useVoidAction(
    'account-payables',
    accountPayable,
  );
  const { resolverButton } = useResolveApproval(
    'account-payables',
    accountPayable,
  );
  const { requestApprovalButton } = useApprovalAction(
    'account-payables',
    accountPayable,
  );

  const { revertDraftButton } = useRevertDraftAction(
    'account-payables',
    accountPayable,
  );
  const onRenderRightComponent = React.useMemo(() => {
    if (accountPayable) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {revertDraftButton}
          {requestApprovalButton}
          {resolverButton}
        </div>
      );
    }
    return undefined;
  }, [
    accountPayable,
    requestApprovalButton,
    resolverButton,
    revertDraftButton,
  ]);
  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!accountPayable}
      customViewName={
        accountPayable &&
        `${t('common:account_payable')} - ${accountPayable.number}`
      }
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={accountPayable}
            title={
              accountPayable ? (
                <TitleContainer>
                  <Text variant="HeadingXSmall">{`${t(
                    'common:account_payable',
                  )} - ${accountPayable.number}`}</Text>
                  <TransactionPillStatus value={accountPayable.status} />
                </TitleContainer>
              ) : (
                t('common:add_extra', { extra: t('common:account_payable') })
              )
            }
            onDelete={onVoid}
            deleteText={t('common:void')}
            isDeleteLoading={voidMutate.isLoading}
            noEdit={!isActive || !permission.update}
            noDelete={editable || !voidPermission}
            onRenderRightComponent={onRenderRightComponent}
          />
        )}
      </FormState>
      <div>
        <InformationField isFilled={!!accountPayable} />
        {methods?.formState?.errors?.paymentItems && (
          <>
            <Text variant="LabelMedium" color="red">
              {methods?.formState?.errors?.paymentItems.message}
            </Text>
            <Separator gap={8} />
          </>
        )}
        <AccountPayableTableForm isFilled={!!accountPayable} />
        <SeparatorVertical $margin={48} />
        <SectionContainer>
          <HalfContainer>
            <SummaryCard />
          </HalfContainer>
          <SeparatorVertical $margin={24} />
          <HalfContainer>
            <PaymentField accountPayable={accountPayable} />
          </HalfContainer>
        </SectionContainer>
        <SeparatorVertical $margin={64} />
      </div>
    </Form>
  );
}
